<!--
 * @Author: Tahiti
 * @Date: 2025-10-21 15:03:16
 * @LastEditors: Tahiti
 * @LastEditTime: 2025-12-17 15:44:17
 * @Description: 
 * @FilePath: /weryai-web/components/Tool/ImageUploadSingle.vue
-->
<template>
    <div class="mt-4">
        <!-- 标题区域：label=undefined显示默认标题，label=''显示占位空间，其他显示自定义标题 -->
        <div v-if="props.label !== ''" class="flex items-center justify-between mb-3 gap-2">
            <label class="text-white text-sm font-semibold" :class="labelStyle">
                {{ props.label || t('tools.imageLabel') }}
            </label>
            <div v-if="is_support_first_last_frame && !props.hide_frame_end_toggle" class="flex justify-center items-center gap-[10px]">
                <span class="text-sub-title flex items-center justify-between text-sm leading-5">
                    {{ t('tools.addEndFrame') }}
                </span>
                <button @click="handleFrameEndToggle" type="button" role="switch"
                    :aria-checked="frame_end_mode" :aria-label="t('tools.addEndFrame')"
                    :disabled="disable_frame_end"
                    class="relative inline-flex h-4 w-7 items-center rounded-full transition-all duration-300"
                    :class="[
                        disable_frame_end ? 'cursor-not-allowed opacity-50 bg-white/10' : 'hover:bg-white/50',
                        !disable_frame_end && frame_end_mode ? 'bg-switch-active-bg' : '',
                        !disable_frame_end && !frame_end_mode ? 'bg-white/10' : '',
                    ]">
                    <span class="inline-block h-3 w-3 transform rounded-full transition-transform" :class="!frame_end_mode
                        ? 'translate-x-0.5 bg-white'
                        : 'translate-x-3.5 bg-switch-active-dot'
                        "></span>
                </button>
            </div>
        </div>
        <!-- 当 label='' 时显示占位空间，用于多图上传时对齐 -->
        <div v-else-if="props.label === ''" class="mb-3" style="height: 22px"></div>
        <div class="w-full flex gap-2">
            <div v-if="!image_url && !singleUploading" @click="triggerSingleFileUpload" @dragover.prevent
                @drop.prevent="handleSingleFileDrop"
                class="h-[127px] w-full max-h-[130px] group text-sub-title hover:text-white flex flex-col items-center justify-center gap-[10px] bg-tool-default-black border border-white/[.06] rounded-[10px] p-[10px] text-center cursor-pointer">


                <div
                    class="flex items-center justify-center flex-col w-[100px] h-[100px] border border-dashed border-white/20 rounded-lg p-2 gap-[10px]">
                    <ui-upload-icon :hoverable="true" />

                    <p class="text-sub-title text-xs group-hover:text-white text-center max-w-full ">
                        {{ t('tools.imageUploadSingle') }}
                    </p>
                </div>
                <input ref="singleFileInput" type="file" accept="image/*" @change="handleSingleFileSelect"
                    class="hidden" />
            </div>
            <div v-else-if="singleUploading"
                class="h-[127px] w-full max-h-[130px] flex flex-col items-center justify-center bg-[rgba(20,20,22,1)] border border-white/[.06] rounded-[10px] p-[10px] text-center cursor-pointer">
                <UiAppLoadingLottie size="30" />
            </div>
            <div v-else-if="image_url"
                class="flex flex-col relative h-[127px] w-full max-h-[130px] items-center justify-center bg-tool-default-black border border-white/[.06] rounded-[10px] p-[10px] text-center cursor-pointer  hover:border-white/30">
                <UiImageLoadLoading :src="image_url" :alt="t('tools.imageLabel') || 'Uploaded image'"
                    class="w-full h-full rounded-lg cursor-pointer " :image-class="'!object-contain'"
                    placeholder-class="bg-[rgba(20,20,22,1)]"
                    @click="emitOpenImagePreview(image_url)" />
                <button type="button" :aria-label="t('common.remove') ||
                    t('common.delete') ||
                    'Remove image'
                    "
                    class="inline-flex rounded-full items-center justify-center whitespace-nowrap text-base font-medium ring-offset-background duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:text-accent-foreground absolute z-[2] top-1 right-1 h-[22px] w-[22px] bg-[#2E2E2E] text-white group-hover:opacity-100 transition-opacity"
                    @click.stop="clearEditImage">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none"
                        class="w-3 h-3">
                        <path d="M9 3.68164L3 9.68164" stroke="white" stroke-width="2" stroke-linecap="round"
                            stroke-linejoin="round" />
                        <path d="M3 3.68164L9 9.68164" stroke="white" stroke-width="2" stroke-linecap="round"
                            stroke-linejoin="round" />
                    </svg>
                </button>
            </div>
        </div>
        <div v-if="isShowInfo" class="text-[#666] text-xs flex gap-1 ml-1 items-center mt-1">
            <el-icon class="!text-sm">
                <Warning />
            </el-icon>
            {{ props.tip || t('upload.errors.fileTooLarge', { maxSize: 10 }) }}
        </div>
    </div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue'
import { Warning } from '@element-plus/icons-vue'
import { useI18n } from '@/composables/useI18n'
import { debounce, noticeMessagePopup, getMaxImageSize } from '@/utils/common'
import { emitOpenImagePreview, showLoginModal } from '~/utils/emitter'
import { isStrictSupportedImageType } from '~/utils/imageValidation'
import axios from 'axios'
import { useUserStore } from '~/stores/user'

const userStore = useUserStore()
const isLoggedIn = computed(() => userStore.isLoggedIn)
const { t, locale } = useI18n()

// 支持自定义标题
const props = withDefaults(
    defineProps<{
        label?: string
        isShowInfo?: boolean
        tip?: string
        labelStyle?: string
        /** 禁用添加尾帧开关（与多分镜互斥时使用） */
        disable_frame_end?: boolean
        /** 上层已提供首尾帧入口时隐藏当前开关 */
        hide_frame_end_toggle?: boolean
    }>(),
    {
        label: undefined, // undefined 表示使用默认标题，'' 表示隐藏标题（但保留占位）
        isShowInfo: true,
        tip: '',
        labelStyle: '',
        disable_frame_end: false,
        hide_frame_end_toggle: false,
    }
)

const frame_end_mode = defineModel('frame_end_mode', {
    type: Boolean,
    default: false,
})
const image_url = defineModel('image_url', {
    type: String,
    default: '',
})

const is_support_first_last_frame = defineModel('is_support_first_last_frame', {
    type: Boolean,
    default: false,
})

const singleFileInput = ref<HTMLInputElement | null>(null)

const handleFrameEndToggle = () => {
    if (props.disable_frame_end) return
    frame_end_mode.value = !frame_end_mode.value
}

const triggerSingleFileUpload = () => {
    if (!isLoggedIn.value) {
        showLoginModal()
        return
    }
    singleFileInput.value?.click()
}

const handleSingleFileDrop = (event: DragEvent) => {
    if (!isLoggedIn.value) {
        showLoginModal()
        return
    }
    const files = event.dataTransfer?.files
    if (files) {
        const fileArray = Array.from(files)
        handleSingleAutoBatchUpload(fileArray)
    }
}

const uploadSingleFileList = ref<File[]>([])
const singleUploading = ref(false)
// single file upload
const handleSingleFileSelect = (event: Event) => {
    const target = event.target as HTMLInputElement
    const files = target.files as FileList
    const fileArray = Array.from(files)
    handleSingleAutoBatchUpload(fileArray)
}
// @ts-ignore
const config = useRuntimeConfig()
let token = ''
let language = 'en'

// upload image
const handleSingleAutoBatchUpload = debounce(async (fileList: File[]) => {
    if (!fileList?.length) return

    // 预校验文件格式（严格图片类型）；不合格的跳过，并且每批次最多提示一次
    const strictImageFiles: File[] = []
    let hasInvalidType = false
    for (const file of fileList) {
        if (!isStrictSupportedImageType(file)) {
            hasInvalidType = true
            continue
        }
        strictImageFiles.push(file)
    }
    if (hasInvalidType) {
        noticeMessagePopup(t('workbench.uploadTypeCheck'), t('common.tip'), 'warning', 3000, '')
    }
    if (!strictImageFiles.length) {
        // 重置 input 值，允许再次选择相同文件
        if (singleFileInput.value) {
            singleFileInput.value.value = ''
        }
        return
    }

    // 校验文件大小（限制为10MB）
    for (const file of strictImageFiles) {
        if (!getMaxImageSize(file, 100)) {
            // 文件大小超过限制，显示错误提示
            noticeMessagePopup(
                t('upload.errors.fileTooLarge', { maxSize: 10 }),
                t('common.tip'),
                'error',
                3000,
                ''
            )
            // 重置 input 值，允许再次选择相同文件
            if (singleFileInput.value) {
                singleFileInput.value.value = ''
            }
            return
        }
    }

    uploadSingleFileList.value = strictImageFiles

    // set skeleton screen number
    const formData = new FormData()
    uploadSingleFileList.value.forEach((fileObj: File) => {
        formData.append('files', fileObj)
    })
    singleUploading.value = true
    try {
        // @ts-ignore
        if (import.meta.client) {
            token = localStorage.getItem('token') || ''
            // 请求语言跟随当前 URL 的 locale，而不是偏好/旧的 localStorage('language')
            language = locale.value || 'en'
        }
        const url = new URL(
            config.public.apiBase + '/api/v1/auth/s3/upload_images'
        )
        url.searchParams.append('app_key', config.public.appKey)

        const res = await axios.post(url.toString(), formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: token,
                'Accept-Language': language,
            },
        })
        const { data, status } = res.data
        if (status === 200) {
            image_url.value = data?.object_url_list?.[0]
            singleUploading.value = false
        } else if ([1005, 4001, 4002].includes(status)) {
            showLoginModal()
        }
    } catch (err) {
        console.error('upload failed:', err)
    } finally {
        singleUploading.value = false
    }
}, 500)

const onImageError = () => {
    noticeMessagePopup(
        t('tools.imageLoadFailed'),
        t('common.tip'),
        'warning',
        3000,
        ''
    )
    image_url.value = ''
}

const clearEditImage = () => {
    image_url.value = ''
}

defineExpose({
    pasteFiles(files: File[]) {
        if (!files?.length) return
        // Keep behavior consistent with other entry points: single upload only.
        handleSingleAutoBatchUpload(files.slice(0, 1))
    },
})

</script>
<style scoped lang="less"></style>
